import type { Rounding } from './types.js';

/**
 * 「素点 − 返し点」の点差を千点単位のポイントに丸める（仕様書 §5.1 Step 2）。
 *
 * 仕様書は `raw = (rawScore - returnPoints) / 1000` を丸める形で書かれているが、
 * ここでは割り算をせず点のまま整数で処理する。0.6 のような値は二進浮動小数点で
 * 正確に表せず、境界判定が方式によっては1ポイントずれうるため。点差は必ず整数な
 * ので、整数部と余りに分ければ誤差は原理的に発生しない。
 *
 * 判定はすべて絶対値で行い、最後に符号を戻す。したがって「切り上げ」は 0 から
 * 離れる方向、「切り捨て」は 0 に近づく方向を意味する。−11.6 は −12 になる。
 *
 * @param diff  素点 − 返し点（点単位の整数。例: 45,200点 − 30,000点 → 15200）
 * @param mode  端数処理の方式
 * @returns     千点単位のポイント（例: 15200 → 15）
 */
export function roundScorePoint(diff: number, mode: Rounding): number {
  if (!Number.isFinite(diff)) {
    throw new TypeError(`点差が数値ではありません: ${diff}`);
  }

  const sign = diff < 0 ? -1 : 1;
  const abs = Math.abs(diff);
  const whole = Math.floor(abs / 1000);
  const rem = abs % 1000; // 0 以上 1000 未満。小数部 0.000〜0.999 に相当

  let rounded: number;
  switch (mode) {
    case 'gosha_rokunyu':
      // 五捨六入: 0.5 以下は切り捨て、0.6 以上は切り上げ。
      // 素点は100点単位なので 0.5 と 0.6 の間（rem が 501〜599）は実際には
      // 発生しないが、1点単位のデータが混入した場合は切り上げ側に倒す。
      rounded = rem <= 500 ? whole : whole + 1;
      break;

    case 'shisha_gonyu':
      // 四捨五入: 0.4 以下は切り捨て、0.5 以上は切り上げ
      rounded = rem < 500 ? whole : whole + 1;
      break;

    case 'ceil':
      rounded = rem > 0 ? whole + 1 : whole;
      break;

    case 'floor':
      rounded = whole;
      break;
  }

  // 0 に -1 を掛けて -0 になるのを避ける
  return rounded === 0 ? 0 : sign * rounded;
}
